// Persistenz des Listenzustands (Sortierung, Spaltenfilter, Seite) im localStorage.
//
// Schlüssel: `mvgen:list-query:v1:{feature}:{tenantSlug}:{userId}` — getrennt nach Mandant und Benutzer,
// damit ein zweiter Benutzer im selben Browser nie fremde Filter sieht. Ohne `userId` wird nichts
// geschrieben und nichts gelesen.
//
// KONVENTION (Löschen): Der Zustand wird AUSSCHLIESSLICH beim ausdrücklichen Abmelden
// (`AuthContext.logout`, inkl. Kontolöschung) über `clearAllListQueries()` entfernt. Nicht in
// `authService.clearAllUserCaches` (läuft auch beim Login) und nicht im 401-Interceptor (Sitzungsablauf
// darf die Filter nicht verwerfen). Der Zugriff ist komplett try/catch-gekapselt (privates Fenster,
// gesperrter Speicher, Quota).

export const LIST_QUERY_KEY_PREFIX = 'mvgen:list-query:';
const VERSION = 'v1';

// Lösch-Epoche: `clearAllListQueries()` erhöht sie. Ein Schreibvorgang trägt die Epoche mit, die beim START
// der zugehörigen Anfrage galt; eine nach dem Abmelden eintreffende Antwort (alte Epoche) schreibt daher
// nichts mehr und stellt die gelöschten Filter nicht wieder her. Der nächste Login startet neue Anfragen
// und damit mit der neuen Epoche — ein Sperr-Flag „bis zum nächsten Login“ ist nicht nötig.
// Bewusstes Restrisiko: Die Epoche gilt pro Tab (Modulzustand). Meldet sich der Nutzer in einem anderen Tab
// ab, kann eine dort noch laufende Antwort den Zustand einmalig neu schreiben.
let clearEpoch = 0;

/** Aktuelle Lösch-Epoche; beim Start einer Anfrage merken und an `writeListQuery` übergeben. */
export const getListQueryEpoch = () => clearEpoch;

/** Baut den Speicherschlüssel; `null`, wenn kein Benutzer bekannt ist. */
export const buildListQueryKey = (feature, tenantSlug, userId) => {
  if (!feature || userId === undefined || userId === null || userId === '') return null;
  return `${LIST_QUERY_KEY_PREFIX}${VERSION}:${feature}:${tenantSlug || ''}:${userId}`;
};

/** Liest den gespeicherten Rohwert (Objekt) oder `null` (nichts gespeichert, kaputt, Speicher gesperrt). */
export const readListQuery = (feature, tenantSlug, userId) => {
  const key = buildListQueryKey(feature, tenantSlug, userId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Schreibt den Listenzustand; ohne Benutzer, bei Speicherfehlern oder wenn seit dem Anfragestart
 * abgemeldet wurde (`epoch` veraltet) still ohne Wirkung.
 *
 * @param {number} [epoch] Epoche beim Anfragestart (`getListQueryEpoch()`); fehlt sie, gilt die aktuelle.
 */
export const writeListQuery = (feature, tenantSlug, userId, query, epoch = clearEpoch) => {
  if (epoch !== clearEpoch) return;
  const key = buildListQueryKey(feature, tenantSlug, userId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(query));
  } catch {
    // Quota/gesperrter Speicher: Persistenz ist eine Komfortfunktion
  }
};

/** Entfernt alle gespeicherten Listenzustände (alle Benutzer/Mandanten/Features dieses Browsers). */
export const clearAllListQueries = () => {
  clearEpoch += 1;
  try {
    const storage = window.localStorage;
    for (let i = storage.length - 1; i >= 0; i--) {
      const key = storage.key(i);
      if (key && key.startsWith(LIST_QUERY_KEY_PREFIX)) storage.removeItem(key);
    }
  } catch {
    // ignorieren
  }
};
